import React, { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { Bell, ChevronRight, Copy, Filter, Search, Send, CheckCircle, CalendarPlus, Flag, RotateCcw } from 'lucide-react'
import { toast } from 'sonner'
import { useAuth } from '../../../contexts/AuthContext'
import { FLAG_COLORS } from '../../../components/sinistre/FlagAttenteCell'

const API = process.env.REACT_APP_BACKEND_URL

const URGENCE_CONFIG = {
  en_retard: { label: 'En retard', color: 'bg-red-100 text-red-700 border-red-200', dot: 'bg-red-500', badge: 'bg-red-500 text-white' },
  aujourd_hui: { label: "Aujourd'hui", color: 'bg-orange-50 text-orange-700 border-orange-200', dot: 'bg-orange-500', badge: 'bg-orange-500 text-white' },
  cette_semaine: { label: 'Cette semaine', color: 'bg-yellow-50 text-yellow-700 border-yellow-200', dot: 'bg-yellow-500', badge: 'bg-amber-500 text-white' },
  a_venir: { label: 'A venir', color: 'bg-slate-50 text-slate-600 border-slate-200', dot: 'bg-slate-400', badge: 'bg-slate-400 text-white' },
}

function CounterCard({ label, count, urgence, active, onClick }) {
  const cfg = URGENCE_CONFIG[urgence] || URGENCE_CONFIG.a_venir
  return (
    <button
      onClick={onClick}
      data-testid={`counter-${urgence}`}
      className={`flex-1 rounded-xl border-2 p-4 text-center transition-all cursor-pointer hover:shadow-md ${active ? 'ring-2 ring-indigo-400 shadow-md' : ''} ${cfg.color}`}
    >
      <p className="text-3xl font-bold">{count}</p>
      <p className="text-xs font-medium mt-1 uppercase tracking-wide">{label}</p>
    </button>
  )
}

function RelanceRow({ relance, onCopy, onAction, navigate }) {
  const cfg = URGENCE_CONFIG[relance.urgence] || URGENCE_CONFIG.a_venir
  const sinistre = relance.sinistres || {}
  const tenant = sinistre.tenants || {}
  const immat = sinistre.vehicules?.immatriculation || sinistre.immatriculation_saisie || ''
  const jours = relance.jours_retard || 0

  return (
    <tr
      className={`border-b border-slate-100 hover:bg-slate-50/80 transition-colors cursor-pointer group ${relance.urgence === 'en_retard' ? 'bg-red-50/30' : ''}`}
      data-testid={`relance-row-${relance.id}`}
    >
      {/* Urgence */}
      <td className="px-4 py-3">
        <div className="flex items-center gap-2">
          <span className={`w-2.5 h-2.5 rounded-full ${cfg.dot} shrink-0`} />
          <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${cfg.badge}`}>
            {cfg.label}
          </span>
          {relance.urgence === 'en_retard' && jours > 0 && (
            <span className="text-[10px] font-bold text-red-600">+{jours}j</span>
          )}
        </div>
        <span className={`mt-1 inline-block text-[10px] px-1.5 py-0.5 rounded ${relance.etape === 'sans_reponse' ? 'bg-blue-50 text-blue-700' : 'bg-amber-50 text-amber-700'}`} data-testid={`relance-etape-${relance.id}`}>
          {relance.etape === 'sans_reponse' ? `Envoyée — sans réponse${relance.jours_sans_reponse != null ? ` depuis ${relance.jours_sans_reponse} j` : ''}` : 'Planifiée'}
        </span>
      </td>
      {/* Client */}
      <td className="px-4 py-3">
        <p className="text-sm font-semibold text-slate-800">{tenant.nom || '--'}</p>
        {sinistre.gestionnaire && <p className="text-[11px] text-slate-400">{sinistre.gestionnaire.prenom} {sinistre.gestionnaire.nom}</p>}
      </td>
      {/* Sinistre */}
      <td className="px-4 py-3">
        <p className="text-sm text-slate-700 font-mono">{sinistre.numero_sinistre_client || sinistre.numero_sinistre || '--'}</p>
        <p className="text-[11px] text-slate-400">{sinistre.nature_sinistre}</p>
      </td>
      {/* Immat */}
      <td className="px-4 py-3">
        <p className="text-sm text-slate-600">{immat || '--'}</p>
      </td>
      {/* Destinataire */}
      <td className="px-4 py-3">
        <p className="text-sm text-slate-700">{relance.destinataire_nom}</p>
        <p className="text-[11px] text-slate-400">{relance.destinataire_type}</p>
      </td>
      {/* Sujet */}
      <td className="px-4 py-3">
        <p className="text-sm text-slate-700 max-w-[200px] truncate">{relance.sujet}</p>
      </td>
      {/* Date echeance */}
      <td className="px-4 py-3">
        <p className="text-sm text-slate-600">
          {(relance.prochaine_relance || relance.date_limite_reponse) ? new Date(relance.prochaine_relance || relance.date_limite_reponse).toLocaleDateString('fr-FR') : '--'}
        </p>
        {relance.etape === 'sans_reponse' && relance.date_envoi && <p className="text-[11px] text-slate-400">envoyée le {new Date(relance.date_envoi).toLocaleDateString('fr-FR')}</p>}
      </td>
      {/* Actions */}
      <td className="px-4 py-3">
        <div className="flex items-center gap-1">
          {relance.etape === 'a_envoyer' ? (
            <button onClick={(e) => { e.stopPropagation(); onAction(relance, 'envoyee') }}
              className="p-1.5 rounded-md hover:bg-blue-100 text-blue-600 transition-colors" title="Marquer envoyée" data-testid={`relance-mark-sent-${relance.id}`}>
              <Send size={14} />
            </button>
          ) : (
            <button onClick={(e) => { e.stopPropagation(); onAction(relance, 'relancer') }}
              className="p-1.5 rounded-md hover:bg-amber-100 text-amber-600 transition-colors" title="Relancer à nouveau (planifier aujourd'hui)" data-testid={`relance-again-${relance.id}`}>
              <RotateCcw size={14} />
            </button>
          )}
          <button onClick={(e) => { e.stopPropagation(); onAction(relance, 'repondue') }}
            className="p-1.5 rounded-md hover:bg-emerald-100 text-emerald-600 transition-colors" title="Marquer répondue" data-testid={`relance-mark-replied-${relance.id}`}>
            <CheckCircle size={14} />
          </button>
          <button onClick={(e) => { e.stopPropagation(); onAction(relance, 'reporter') }}
            className="p-1.5 rounded-md hover:bg-slate-100 text-slate-500 transition-colors" title="Reporter de 7 jours" data-testid={`relance-postpone-${relance.id}`}>
            <CalendarPlus size={14} />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); onCopy(relance) }}
            className="p-1.5 rounded-md hover:bg-indigo-100 text-indigo-500 transition-colors"
            title="Copier le texte email"
            data-testid={`copy-relance-${relance.id}`}
          >
            <Copy size={14} />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); navigate(`/c2/sinistre/${sinistre.id}`) }}
            className="p-1.5 rounded-md hover:bg-indigo-100 text-indigo-500 transition-colors"
            title="Ouvrir le sinistre"
            data-testid={`goto-sinistre-${relance.id}`}
          >
            <ChevronRight size={14} />
          </button>
        </div>
      </td>
    </tr>
  )
}

export default function RelancesDashboard() {
  const { profile } = useAuth()
  const [relances, setRelances] = useState([])
  const [dossiersAttente, setDossiersAttente] = useState([])
  const [filterEtape, setFilterEtape] = useState('')
  const [compteurs, setCompteurs] = useState({ en_retard: 0, aujourd_hui: 0, cette_semaine: 0, a_venir: 0, total: 0 })
  const [loading, setLoading] = useState(true)
  const [filterUrgence, setFilterUrgence] = useState(null)
  const [filterClient, setFilterClient] = useState('')
  const [search, setSearch] = useState('')
  const [copiedId, setCopiedId] = useState(null)
  const navigate = useNavigate()

  const fetchData = useCallback(async () => {
    try {
      const params = new URLSearchParams()
      if (profile?.id) params.set('user_id', profile.id)
      if (profile?.role) params.set('role', profile.role)
      const res = await fetch(`${API}/api/relances/dashboard?${params}`)
      const data = await res.json()
      setRelances(data.relances || [])
      setDossiersAttente(data.dossiers_attente || [])
      setCompteurs(data.compteurs || {})
    } catch (err) {
      toast.error('Erreur chargement des relances')
    } finally {
      setLoading(false)
    }
  }, [profile?.id, profile?.role])

  useEffect(() => { if (profile) fetchData() }, [fetchData, profile])

  async function handleAction(relance, action) {
    const today = new Date().toISOString().split('T')[0]
    let body = {}
    if (action === 'envoyee') body = { statut: 'envoyee', date_envoi: new Date().toISOString() }
    if (action === 'repondue') body = { statut: 'repondue', date_reponse: new Date().toISOString() }
    if (action === 'relancer') body = { statut: 'a_envoyer', prochaine_relance: today }
    if (action === 'reporter') {
      const base = relance.prochaine_relance || relance.date_limite_reponse || today
      const d = new Date(base); d.setDate(d.getDate() + 7)
      body = { prochaine_relance: d.toISOString().split('T')[0] }
    }
    try {
      const res = await fetch(`${API}/api/relances/${relance.id}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) })
      if (!res.ok) throw new Error()
      toast.success({ envoyee: 'Relance marquée envoyée', repondue: 'Relance clôturée (répondue)', relancer: 'Nouvelle relance planifiée aujourd\'hui', reporter: 'Relance reportée de 7 jours' }[action])
      fetchData()
    } catch { toast.error('Erreur mise à jour relance') }
  }

  const handleCopy = useCallback((relance) => {
    const text = `${relance.sujet}\n\n${relance.contenu}`
    navigator.clipboard.writeText(text).then(() => {
      setCopiedId(relance.id)
      toast.success('Texte email copie')
      setTimeout(() => setCopiedId(null), 2000)
    })
  }, [])

  // Unique client names for filter
  const clients = [...new Set(relances.map(r => r.sinistres?.tenants?.nom).filter(Boolean))].sort()

  // Apply filters
  const filtered = relances.filter(r => {
    if (filterUrgence && r.urgence !== filterUrgence) return false
    if (filterEtape && r.etape !== filterEtape) return false
    if (filterClient && r.sinistres?.tenants?.nom !== filterClient) return false
    if (search) {
      const s = search.toLowerCase()
      const num = (r.sinistres?.numero_sinistre_client || r.sinistres?.numero_sinistre || '').toLowerCase()
      const dest = (r.destinataire_nom || '').toLowerCase()
      const sujet = (r.sujet || '').toLowerCase()
      const immat = (r.sinistres?.vehicules?.immatriculation || r.sinistres?.immatriculation_saisie || '').toLowerCase()
      if (!num.includes(s) && !dest.includes(s) && !sujet.includes(s) && !immat.includes(s)) return false
    }
    return true
  })

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full p-12" data-testid="relances-loading">
        <div className="animate-spin w-8 h-8 border-2 border-indigo-500 border-t-transparent rounded-full" />
      </div>
    )
  }

  return (
    <div className="p-6 max-w-[1400px] mx-auto" data-testid="relances-dashboard">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-3" data-testid="relances-title">
          <Bell size={24} className="text-indigo-500" />
          Tableau de bord des relances
        </h1>
        <p className="text-sm text-slate-500 mt-1">Relances planifiées, relances envoyées sans réponse et dossiers marqués en attente — {['master_admin', 'c2_admin'].includes(profile?.role) ? 'tous clients' : 'votre portefeuille'}</p>
      </div>

      {/* Counters */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
        <CounterCard label="En retard" count={compteurs.en_retard} urgence="en_retard"
          active={filterUrgence === 'en_retard'} onClick={() => setFilterUrgence(filterUrgence === 'en_retard' ? null : 'en_retard')} />
        <CounterCard label="Aujourd'hui" count={compteurs.aujourd_hui} urgence="aujourd_hui"
          active={filterUrgence === 'aujourd_hui'} onClick={() => setFilterUrgence(filterUrgence === 'aujourd_hui' ? null : 'aujourd_hui')} />
        <CounterCard label="Cette semaine" count={compteurs.cette_semaine} urgence="cette_semaine"
          active={filterUrgence === 'cette_semaine'} onClick={() => setFilterUrgence(filterUrgence === 'cette_semaine' ? null : 'cette_semaine')} />
        <CounterCard label="A venir" count={compteurs.a_venir} urgence="a_venir"
          active={filterUrgence === 'a_venir'} onClick={() => setFilterUrgence(filterUrgence === 'a_venir' ? null : 'a_venir')} />
        <CounterCard label="Total" count={compteurs.total} urgence="a_venir"
          active={!filterUrgence} onClick={() => setFilterUrgence(null)} />
      </div>

      {/* Filters bar */}
      <div className="flex items-center gap-3 mb-4">
        <div className="relative flex-1 max-w-xs">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            type="text"
            placeholder="Rechercher sinistre, destinataire, immat..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            data-testid="relances-search"
            className="w-full pl-9 pr-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-400 bg-white"
          />
        </div>
        <div className="relative">
          <Filter size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <select
            value={filterClient}
            onChange={e => setFilterClient(e.target.value)}
            data-testid="relances-filter-client"
            className="pl-9 pr-6 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-400 bg-white appearance-none cursor-pointer"
          >
            <option value="">Tous les clients</option>
            {clients.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <div className="flex items-center gap-1 bg-white border border-slate-200 rounded-lg p-0.5" data-testid="relances-filter-etape">
          {[['', 'Toutes'], ['a_envoyer', `Planifiées (${compteurs.a_envoyer || 0})`], ['sans_reponse', `Sans réponse (${compteurs.sans_reponse || 0})`]].map(([v, l]) => (
            <button key={v} onClick={() => setFilterEtape(v)} data-testid={`etape-${v || 'all'}`}
              className={`px-3 py-1.5 text-xs rounded-md font-medium ${filterEtape === v ? 'bg-indigo-600 text-white' : 'text-slate-600 hover:bg-slate-50'}`}>{l}</button>
          ))}
        </div>
        <p className="text-xs text-slate-400 ml-auto">{filtered.length} relance{filtered.length > 1 ? 's' : ''}</p>
      </div>

      {/* Table */}
      {filtered.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-xl border border-slate-200" data-testid="relances-empty">
          <Bell size={40} className="mx-auto text-slate-300 mb-3" />
          <p className="text-slate-500 font-medium">Aucune relance en attente</p>
          <p className="text-xs text-slate-400 mt-1">Toutes les relances sont a jour</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="relances-table">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50/50">
                  <th className="px-4 py-3 text-left text-[10px] font-bold text-slate-500 uppercase tracking-wider">Urgence</th>
                  <th className="px-4 py-3 text-left text-[10px] font-bold text-slate-500 uppercase tracking-wider">Client</th>
                  <th className="px-4 py-3 text-left text-[10px] font-bold text-slate-500 uppercase tracking-wider">Sinistre</th>
                  <th className="px-4 py-3 text-left text-[10px] font-bold text-slate-500 uppercase tracking-wider">Immat</th>
                  <th className="px-4 py-3 text-left text-[10px] font-bold text-slate-500 uppercase tracking-wider">Destinataire</th>
                  <th className="px-4 py-3 text-left text-[10px] font-bold text-slate-500 uppercase tracking-wider">Sujet</th>
                  <th className="px-4 py-3 text-left text-[10px] font-bold text-slate-500 uppercase tracking-wider">Echeance</th>
                  <th className="px-4 py-3 text-left text-[10px] font-bold text-slate-500 uppercase tracking-wider w-[80px]">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(r => (
                  <RelanceRow key={r.id} relance={r} onCopy={handleCopy} onAction={handleAction} navigate={navigate} />
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Dossiers marques en attente (drapeau) sans relance active */}
      <div className="mt-8" data-testid="dossiers-attente-section">
        <h2 className="text-sm font-bold text-slate-700 flex items-center gap-2 mb-3">
          <Flag size={15} className="text-amber-500" />
          Dossiers marqués « en attente » sans relance planifiée
          <span className="px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 text-[11px]" data-testid="dossiers-attente-count">{dossiersAttente.filter(d => !filterClient || d.tenants?.nom === filterClient).length}</span>
        </h2>
        {dossiersAttente.length === 0 ? (
          <p className="text-xs text-slate-400 bg-white border border-slate-200 rounded-xl p-4">Aucun dossier en attente sans relance.</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-2">
            {dossiersAttente.filter(d => !filterClient || d.tenants?.nom === filterClient).map(d => (
              <button key={d.id} onClick={() => navigate(`/c2/sinistre/${d.id}`)}
                className="text-left bg-white border border-amber-200 rounded-xl px-4 py-3 hover:border-amber-400 hover:shadow-sm transition-all" data-testid={`dossier-attente-${d.id}`}>
                <div className="flex items-center justify-between gap-2">
                  <span className="flex items-center gap-1.5">
                    <span className="w-2.5 h-2.5 rounded-full ring-1 ring-slate-300 shrink-0" style={{ backgroundColor: FLAG_COLORS.find(c => c.id === d.flag_attente)?.hex || '#F59E0B' }} title={FLAG_COLORS.find(c => c.id === d.flag_attente)?.label || 'En attente'} />
                    <span className="font-mono text-xs font-bold text-indigo-700">{d.numero_sinistre_client || d.numero_sinistre}</span>
                  </span>
                  <span className="text-[11px] text-slate-500">{d.tenants?.nom}</span>
                </div>
                <p className="text-xs text-slate-600 mt-1 truncate">{d.vehicules?.immatriculation || d.immatriculation_saisie || ''} {d.nature_sinistre ? `· ${d.nature_sinistre}` : ''}</p>
                {d.note_gestionnaire && <p className="text-[11px] text-slate-400 mt-1 line-clamp-2 italic">{d.note_gestionnaire}</p>}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
